/**
 * Tier-1 client for the dunning queue (redesign phase 4d, catalogue C1).
 *
 * Separate from `webhook-admin.ts` for the same reason that one is separate
 * from `promo-admin.ts`: a different operator job over a different table.
 * Webhooks are money-path *failures*; dunning is customers whose payment has
 * gone wrong and who are, right now, being refused AI because of it.
 */
import { request } from '@papan/api-client'

/** The outstanding invoice behind a dunning row, when there is one. `null`
 *  means nothing is outstanding — a disputed subscription whose invoice was
 *  paid is exactly that case, and a zero total would claim a debt that does
 *  not exist. */
export interface DunningInvoice {
  id: string
  status: string
  /** Minor units of `currency`, as stored. Never divide by a hardcoded 100 —
   *  the exponent is the currency's (CLAUDE.md §Whitelabel). */
  totalMinor: number
  currency: string
  /** How many times the provider has tried to collect. */
  attemptCount: number
  dueAt: string | null
  /** Deep link into the provider's own invoice page. Stripe is authoritative
   *  for invoice actions; we mirror and link rather than reimplement. */
  hostedInvoiceUrl: string | null
}

export interface DunningRow {
  id: string
  tenantId: string
  tenantName: string | null
  status: string
  planId: string
  planName: string
  seatCount: number | null
  /** Unpaid past this ⇒ the subscription is cancelled and entitlements revert
   *  to free. Stripe drives that on its own schedule; we do not race it. */
  graceUntil: string | null
  /** A chargeback is open. Persists across re-mirrors on purpose, and refuses
   *  AI with `COST_LIMIT_DISPUTED` while it is set. */
  disputedAt: string | null
  cancelAtPeriodEnd: boolean
  currentPeriodEnd: string | null
  invoice: DunningInvoice | null
}

export const dunningAdminApi = {
  /** Past-due, disputed and in-grace subscriptions, most urgent first. */
  list: () =>
    request<{ subscriptions: DunningRow[] }>('/api/admin/billing/dunning'),
}
